"use client";

import { FC, HTMLAttributes, useContext, useMemo, useState } from "react";

import { useInfiniteQuery } from "@tanstack/react-query";
import { getImageUrl } from "@/utils/media.helper";
import { blogInfiniteQuery } from "@/api/blog";
import { ComboboxData } from "@mantine/core";
import { isEmpty } from "lodash";
import clsx from "clsx";

import { leadershipBlogsSubCategoryContext } from "@/stores/LeadershipBlogsSubCategory.context";
import useBlogsSubCategory from "@/hooks/useBlogsSubCategory";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

import BlogCard, { IBlogCard } from "@/components/cards/BlogCard";
import BlogCardSkeleton from "@/components/skeletons/BlogCardSkeleton";
import TButton from "@/components/atoms/buttons/TButton";
import LatestLeadershipBlogSkeleton from "@/components/skeletons/LatestLeadershipBlogSkeleton";
// Multiselect: Please remove and uncomment below lines
import TDropDown from "@/components/atoms/drop-down/TDropDown";
// import TMultiDropDown, {
//   IMultiDropDownOption,
// } from "@/components/atoms/multi-dropdown/TMultiDropDown";

// ----------------------------------------------------------------------------

export type IBlogsWithFilterSectionProps = HTMLAttributes<HTMLElement> & {
  initialBlogCount?: number;
  /**
   * Which Strapi blog category this listing filters on. Drives the API
   * filter, the link prefix, and the button IDs so the same section
   * works for both /leadership-insights and /technical-blogs.
   */
  category?: "leadership" | "technical";
};

const BlogsWithFilterSection: FC<IBlogsWithFilterSectionProps> = (props) => {
  const { initialBlogCount, category = "leadership", className, ...other } =
    props;
  // Detail URLs use the new /blog/<category>/<slug> shape. The legacy
  // /technical-blogs/* and /leadership-insights/* slug URLs still 200
  // (via permanent redirect → here), but new internal links emit the
  // canonical path so crawlers see one consistent shape.
  const linkPrefix =
    category === "technical" ? "/blog/technical" : "/blog/leadership";

  // Default to one full backend page so the initial display matches the
  // 9 cards we already fetched. Each Load More click reveals the next 9.
  // Keeping `initialBlogCount` overridable for any consumer that wants a
  // narrower hero strip.
  const PAGE_SIZE = 9;
  const [showBlogs, setShowBlogs] = useState<number>(
    initialBlogCount ?? PAGE_SIZE,
  );

  // Blogs SubCategory
  const { blogsByFunctions, blogsByProcesses, blogsByRoles } =
    useBlogsSubCategory();

  // Multiselect: Please remove below line
  // Add First value as "ALL" in all SubCategories for Filter Option
  blogsByFunctions?.unshift("ALL");
  blogsByProcesses?.unshift("ALL");
  blogsByRoles?.unshift("ALL");

  const {
    activeBlogsByFunctions,
    activeBlogsByRoles,
    activeBlogsByProcesses,
    setActiveBlogsByFunctions,
    setActiveBlogsByRoles,
    setActiveBlogsByProcesses,
  } = useContext(leadershipBlogsSubCategoryContext);

  const { data, fetchNextPage, hasNextPage, isError, isLoading, isFetching } =
    // Multiselect: Please remove and uncomment below lines
    useInfiniteQuery({
      ...blogInfiniteQuery.all({
        sort: "publish_date:DESC",
        populate: "deep",
        pagination: {
          pageSize: 9,
        },
        filters: {
          category: {
            $eq: category,
          },
          ...(activeBlogsByFunctions !== "ALL" && {
            blogs_by_functions: {
              name: { $eq: activeBlogsByFunctions },
            },
          }),
          ...(activeBlogsByRoles !== "ALL" && {
            blogs_by_roles: {
              name: { $eq: activeBlogsByRoles },
            },
          }),
          ...(activeBlogsByProcesses !== "ALL" && {
            blogs_by_processes: {
              name: { $eq: activeBlogsByProcesses },
            },
          }),
        },
      }),
    });
  // useInfiniteQuery({
  //   ...blogInfiniteQuery.all({
  //     sort: "publish_date:DESC",
  //     populate: "deep",
  //     pagination: {
  //       pageSize: 5,
  //     },
  //     filters: {
  //       category: {
  //         $eq: "leadership",
  //       },
  //       ...(!isEmpty(activeBlogsByFunctions) && {
  //         blogs_by_functions: {
  //           name: {
  //             $in: activeBlogsByFunctions,
  //           },
  //         },
  //       }),
  //       ...(!isEmpty(activeBlogsByRoles) && {
  //         blogs_by_roles: {
  //           name: {
  //             $in: activeBlogsByRoles,
  //           },
  //         },
  //       }),
  //       ...(!isEmpty(activeBlogsByProcesses) && {
  //         blogs_by_processes: {
  //           name: {
  //             $in: activeBlogsByProcesses,
  //           },
  //         },
  //       }),
  //     },
  //   }),
  // });

  const infiniteBlogs = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.flat());
  }, [data?.pages]);

  // Split the listing into the (optional) featured card + remaining grid
  // entries. Computing both from the same `featuredEntry` reference makes
  // sure the grid excludes whatever the featured card renders, and DOES
  // NOT drop index 0 when no blog is featured (which was hiding the
  // newest blog on the page).
  const featuredEntry = useMemo(
    () => infiniteBlogs?.find((b) => b.attributes.featured === true),
    [infiniteBlogs],
  );
  const gridBlogs = useMemo(
    () =>
      featuredEntry
        ? (infiniteBlogs ?? []).filter((b) => b.id !== featuredEntry.id)
        : (infiniteBlogs ?? []),
    [infiniteBlogs, featuredEntry],
  );

  const loadMore = () => {
    // Bump by one full backend page so each click reveals the same number
    // of cards we just fetched (or initialBlogCount if the consumer is
    // using a narrower hero strip).
    setShowBlogs((pre) => pre + (initialBlogCount ?? PAGE_SIZE));
    hasNextPage && fetchNextPage();
  };

  return (
    <section
      id="latest-blogs"
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      {/* SubCategory filter section */}
      {/* Multiselect: Please remove and uncomment below lines */}
      {((blogsByFunctions && blogsByFunctions.length > 1) ||
        (blogsByProcesses && blogsByProcesses.length > 1) ||
        (blogsByRoles && blogsByRoles.length > 1)) && (
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between lg:items-end">
          <div className="flex flex-col flex-wrap gap-4 md:flex-row">
            {/* Filter by Function */}
            {blogsByFunctions && blogsByFunctions.length > 1 && (
              <TDropDown
                label="By Function"
                data={blogsByFunctions as ComboboxData}
                activeValue={activeBlogsByFunctions}
                setActiveValue={setActiveBlogsByFunctions}
              />
            )}

            {/* Filter by Role */}
            {blogsByRoles && blogsByRoles.length > 1 && (
              <TDropDown
                label="By Role"
                data={blogsByRoles as ComboboxData}
                activeValue={activeBlogsByRoles}
                setActiveValue={setActiveBlogsByRoles}
              />
            )}

            {/* Filter by Process */}
            {blogsByProcesses && blogsByProcesses.length > 1 && (
              <TDropDown
                label="By Process"
                data={blogsByProcesses as ComboboxData}
                activeValue={activeBlogsByProcesses}
                setActiveValue={setActiveBlogsByProcesses}
              />
            )}
          </div>

          {/* Reset Filter Button — light_primary pill matches the
              editorial card aesthetic (subtle, secondary). Sits next to
              the filter dropdowns without competing for attention. */}
          <div className="mt-4 md:mt-0">
            <TButton
              id={`reset-all-${category}-filters-button`}
              variant="light_primary"
              radius="full"
              onClick={() => {
                setActiveBlogsByFunctions("ALL");
                setActiveBlogsByRoles("ALL");
                setActiveBlogsByProcesses("ALL");
              }}
            >
              Reset Filters
            </TButton>
          </div>
        </div>
      )}
      {/* {((blogsByFunctions && blogsByFunctions.length > 0) ||
        (blogsByProcesses && blogsByProcesses.length > 0) ||
        (blogsByRoles && blogsByRoles.length > 0)) && (
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between lg:items-end">
          <div className="flex flex-col flex-wrap gap-4 md:flex-row">
            {blogsByFunctions && blogsByFunctions.length > 0 && (
              <TMultiDropDown
                label="By Function"
                options={
                  blogsByFunctions.map((name) => ({
                    label: name,
                    value: name,
                  })) as IMultiDropDownOption[]
                }
                values={activeBlogsByFunctions}
                onOptionsChange={(v) => setActiveBlogsByFunctions(v)}
              />
            )}
            {blogsByRoles && blogsByRoles.length > 0 && (
              <TMultiDropDown
                label="By Role"
                options={
                  blogsByRoles.map((name) => ({
                    label: name,
                    value: name,
                  })) as IMultiDropDownOption[]
                }
                values={activeBlogsByRoles}
                onOptionsChange={(v) => setActiveBlogsByRoles(v)}
              />
            )}
            {blogsByProcesses && blogsByProcesses.length > 0 && (
              <TMultiDropDown
                label="By Process"
                options={
                  blogsByProcesses.map((name) => ({
                    label: name,
                    value: name,
                  })) as IMultiDropDownOption[]
                }
                values={activeBlogsByProcesses}
                onOptionsChange={(v) => setActiveBlogsByProcesses(v)}
              />
            )}
          </div>

          <div className="mt-4 md:mt-0">
            <TButton
              id={`reset-all-${category}-filters-button`}
              variant="light_primary"
              radius="full"
              onClick={() => {
                setActiveBlogsByFunctions([]);
                setActiveBlogsByRoles([]);
                setActiveBlogsByProcesses([]);
              }}
            >
              Reset Filters
            </TButton>
          </div>
        </div>
      )} */}

      {/* Featured card: only the blog explicitly flagged as `featured:
          true` in Strapi gets the big card treatment. If none is flagged,
          we skip the featured block entirely and everything lands in the
          normal grid below. `featuredEntry` is computed above and shared
          with the grid filter so we never accidentally hide a blog. */}
      {featuredEntry && (() => {
        const f = featuredEntry.attributes;
        return (
          <BlogCard
            link={`${linkPrefix}/${f.slug}`}
            image={{
              src: getImageUrl(f.image),
              alt: f.image?.name ?? "",
            }}
            imageClass="w-full"
            publishDate={f.publish_date}
            readingTime={f.reading_time}
            heading={f.title}
            description={
              f.description ? (
                <BlocksRenderer content={f.description} />
              ) : undefined
            }
            author={{
              image: getImageUrl(f.author?.data?.attributes.image),
              name: f.author?.data?.attributes.name,
            }}
            category={f.category}
            contentWrapperClass="col-span-full !px-0 !pb-0 !pt-6 lg:col-span-5"
            figureClass="col-span-full flex items-center lg:col-span-7"
            className="grid grid-cols-6 gap-5 lg:grid-cols-12 lg:gap-7"
            showReadArticleButton={true}
          />
        );
      })()}

      <div className="bg-white">
        {isLoading ? (
          <>
            <LatestLeadershipBlogSkeleton />
            <div
              className={clsx(
                "mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6",
              )}
            >
              {Array.from({ length: showBlogs })
                .slice(1)
                .map((_, index) => (
                  <BlogCardSkeleton key={index} />
                ))}
            </div>
          </>
        ) : isError ? (
          <p className="mt-10 text-center text-lg font-medium text-gray-500">
            Something went wrong. Please try again later.
          </p>
        ) : isEmpty(infiniteBlogs) || !infiniteBlogs ? (
          <p className="mt-10 text-center text-lg font-medium text-gray-500">
            No blogs found.
          </p>
        ) : (
          <>
            {gridBlogs.length > 0 && (
              <div className="grid grid-cols-1 gap-x-5 gap-y-5 pt-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-6 lg:pt-14 xl:pt-[70px]">
                {gridBlogs.slice(0, showBlogs).map(({ id, attributes }) => (
                  <BlogCard
                    key={id}
                    link={`${linkPrefix}/${attributes.slug}`}
                    publishDate={attributes.publish_date}
                    readingTime={attributes.reading_time}
                    description={
                      attributes.description ? (
                        <BlocksRenderer content={attributes.description} />
                      ) : undefined
                    }
                    image={{
                      src: getImageUrl(attributes.image),
                      alt: attributes.image?.name ?? "",
                    }}
                    heading={attributes.title}
                    author={{
                      image: getImageUrl(
                        attributes.author?.data?.attributes.image,
                      ),
                      name: attributes.author?.data?.attributes.name,
                    }}
                    variant="white"
                    showReadArticleButton={true}
                  />
                ))}
              </div>
            )}

            {(showBlogs < gridBlogs.length || hasNextPage) && (
              <div className="mt-8 text-center md:mt-10 xl:mt-[60px]">
                {/* Outlined pill — matches the editorial card aesthetic
                    (subtle primary border, transparent bg, fills on hover).
                    Sits at the bottom of the grid without competing with
                    the cards above. */}
                <TButton
                  id={`load-more-${category}-blogs-button`}
                  variant="outline-brand"
                  radius="full"
                  onClick={() => loadMore()}
                  disabled={isFetching || isLoading}
                >
                  {isFetching ? "Loading…" : "Load more"}
                </TButton>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default BlogsWithFilterSection;
